import React, { useEffect, useRef, useState, useCallback } from "react";

type VideoSnap = {
  id: string | null;
  paused: boolean;
  positionSec: number;
  startedAtMs: number;
  version: number;
} | null;

type Props = {
  snapshot: VideoSnap;
  serverTimeMs: number;
  clockSkewMsRef: React.MutableRefObject<number>;
  onVideoCmd?: (cmd: "LOAD" | "PLAY" | "PAUSE" | "SEEK", payload?: { id?: string; pos?: number }) => void;
};

const DRIFT_HARD_SNAP_MS = 2000; // 2 seconds
const DRIFT_SOFT_SNAP_MS = 500;  // 0.5 seconds
const SYNC_CHECK_INTERVAL = 1000; // Check sync every second

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export default function YouTubePane({ snapshot, serverTimeMs, clockSkewMsRef, onVideoCmd }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const applyingRemoteRef = useRef(false);
  const lastVersionRef = useRef<number>(-1);
  const currentVideoIdRef = useRef<string | null>(null);
  const syncCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [ytReady, setYtReady] = useState<boolean>(() => !!(window.YT && window.YT.Player));
  const [playerReady, setPlayerReady] = useState<boolean>(false);
  const [urlInput, setUrlInput] = useState<string>("");
  const [playerState, setPlayerState] = useState<string>("unstarted");

  // Load YouTube IFrame API
  useEffect(() => {
    if (ytReady) return;
    
    // Check if script already exists
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      if (window.YT && window.YT.Player) {
        setYtReady(true);
        return;
      }
    }

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    const originalCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      setYtReady(true);
      if (originalCallback) originalCallback();
    };

    return () => {
      // Cleanup
      if (window.onYouTubeIframeAPIReady === window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = originalCallback || (() => {});
      }
    };
  }, []);

  // Create YouTube player
  useEffect(() => {
    if (!ytReady || !containerRef.current || playerRef.current) return;

    // Clear any existing content
    containerRef.current.innerHTML = '';

    const playerDiv = document.createElement('div');
    playerDiv.id = `yt-player-${Date.now()}`;
    containerRef.current.appendChild(playerDiv);

    try {
      playerRef.current = new window.YT.Player(playerDiv, {
        height: "100%",
        width: "100%",
        playerVars: { 
          autoplay: 0, 
          controls: 1,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          fs: 1,
          playsinline: 1
        },
        events: {
          onReady: (event: any) => {
            console.log("YouTube player ready");
            setPlayerReady(true);
            setPlayerState("ready");
          },
          onStateChange: (event: any) => {
            if (applyingRemoteRef.current) {
              console.log("Ignoring state change - applying remote state");
              return;
            }

            const stateNames: Record<number, string> = {
              '-1': 'unstarted',
              '0': 'ended',
              '1': 'playing', 
              '2': 'paused',
              '3': 'buffering',
              '5': 'cued'
            };
            
            const stateName = stateNames[event.data] || 'unknown';
            setPlayerState(stateName);
            console.log("Player state changed:", stateName, event.data);

            // Handle user-initiated actions
            if (event.data === 1) { // Playing
              console.log("User played video");
              onVideoCmd?.("PLAY");
            } else if (event.data === 2) { // Paused
              const pos = playerRef.current?.getCurrentTime?.() || 0;
              console.log("User paused video at", pos);
              onVideoCmd?.("PAUSE", { pos });
            } else if (event.data === 0) { // Ended
              console.log("Video ended");
              // Notify server that video ended for queue progression
              // The server should handle this via VIDEO_ENDED message type
            }
          },
          onError: (event: any) => {
            console.error("YouTube player error:", event.data);
            setPlayerState("error");
          }
        },
      });
    } catch (error) {
      console.error("Error creating YouTube player:", error);
    }

    return () => {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.warn("Error destroying player:", e);
        }
      }
      playerRef.current = null;
      setPlayerReady(false);
    };
  }, [ytReady]);

  // Continuous sync checking
  useEffect(() => {
    if (!playerReady || !snapshot || snapshot.paused) {
      if (syncCheckIntervalRef.current) {
        clearInterval(syncCheckIntervalRef.current);
        syncCheckIntervalRef.current = null;
      }
      return;
    }

    syncCheckIntervalRef.current = setInterval(() => {
      if (!playerRef.current || applyingRemoteRef.current || !snapshot || snapshot.paused) return;

      const currentTime = playerRef.current.getCurrentTime?.() || 0;
      const expectedTime = computeExpectedPosition(snapshot, serverTimeMs, clockSkewMsRef.current);
      const drift = Math.abs(currentTime - expectedTime);

      if (drift > DRIFT_SOFT_SNAP_MS / 1000) {
        console.log(`Drift detected: ${drift.toFixed(2)}s, syncing to ${expectedTime.toFixed(2)}s`);
        applyingRemoteRef.current = true;
        try {
          playerRef.current.seekTo?.(expectedTime, true);
        } finally {
          setTimeout(() => {
            applyingRemoteRef.current = false;
          }, 100);
        }
      }
    }, SYNC_CHECK_INTERVAL);

    return () => {
      if (syncCheckIntervalRef.current) {
        clearInterval(syncCheckIntervalRef.current);
        syncCheckIntervalRef.current = null;
      }
    };
  }, [playerReady, snapshot, serverTimeMs]);

  // Apply server state changes
  useEffect(() => {
    if (!playerReady || !playerRef.current || !snapshot) return;

    console.log("Applying snapshot:", snapshot);

    // Handle video loading
    if (snapshot.id && snapshot.id !== currentVideoIdRef.current) {
      console.log("Loading new video:", snapshot.id);
      applyingRemoteRef.current = true;
      
      try {
        if (snapshot.id !== currentVideoIdRef.current) {
          playerRef.current.loadVideoById?.(snapshot.id, snapshot.positionSec || 0);
          currentVideoIdRef.current = snapshot.id;
        }
      } catch (error) {
        console.error("Error loading video:", error);
      } finally {
        setTimeout(() => {
          applyingRemoteRef.current = false;
        }, 1000); // Longer timeout for loading
      }
      return; // Let the video load before applying other state
    }

    // Skip if this is an older version
    if (snapshot.version != null && lastVersionRef.current >= 0 && snapshot.version <= lastVersionRef.current) {
      console.log("Skipping older version:", snapshot.version, "vs", lastVersionRef.current);
      return;
    }
    lastVersionRef.current = snapshot.version ?? (lastVersionRef.current + 1);

    // Apply playback state
    const expectedPosition = computeExpectedPosition(snapshot, serverTimeMs, clockSkewMsRef.current);
    const currentPosition = playerRef.current.getCurrentTime?.() || 0;
    const drift = Math.abs(currentPosition - expectedPosition);

    applyingRemoteRef.current = true;
    
    try {
      // Seek if needed
      if (drift > DRIFT_SOFT_SNAP_MS / 1000) {
        console.log("Seeking to:", expectedPosition, "from:", currentPosition);
        playerRef.current.seekTo?.(expectedPosition, true);
      }

      // Apply play/pause state
      if (snapshot.paused) {
        if (playerRef.current.getPlayerState?.() === 1) { // Currently playing
          console.log("Pausing video");
          playerRef.current.pauseVideo?.();
        }
      } else {
        if (playerRef.current.getPlayerState?.() !== 1) { // Not currently playing
          console.log("Playing video");
          playerRef.current.playVideo?.();
        }
      }
    } catch (error) {
      console.error("Error applying video state:", error);
    } finally {
      setTimeout(() => {
        applyingRemoteRef.current = false;
      }, 200);
    }
  }, [snapshot, serverTimeMs, playerReady]);

  // Manual control functions
  const handleSeek = useCallback((seconds: number) => {
    if (!playerReady) return;
    console.log("Manual seek to:", seconds);
    onVideoCmd?.("SEEK", { pos: seconds });
  }, [playerReady, onVideoCmd]);

  const handlePlayPause = useCallback(() => {
    if (!playerReady || !snapshot) return;
    
    if (snapshot.paused) {
      console.log("Manual play");
      onVideoCmd?.("PLAY");
    } else {
      const pos = playerRef.current?.getCurrentTime?.() || 0;
      console.log("Manual pause at:", pos);
      onVideoCmd?.("PAUSE", { pos });
    }
  }, [playerReady, snapshot, onVideoCmd]);

  const handleLoadUrl = useCallback(() => {
    const id = extractVideoId(urlInput);
    if (id) {
      console.log("Loading video from URL:", id);
      onVideoCmd?.("LOAD", { id });
      setUrlInput("");
    }
  }, [urlInput, onVideoCmd]);

  // Extract video ID from various YouTube URL formats
  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
      /^[a-zA-Z0-9_-]{11}$/ // Direct video ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1] || match[0];
      }
    }
    return null;
  };

  const currentPosition = playerRef.current?.getCurrentTime?.() || 0;
  const duration = playerRef.current?.getDuration?.() || 0;
  const progress = duration > 0 ? (currentPosition / duration) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center text-white shadow-lg">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 15l5.19-3L10 9v6z"/>
              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" fill="none" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Video Player</h2>
            <p className="text-slate-400">
              {snapshot?.id ? `Playing: ${snapshot.id}` : "No video loaded"}
              {ytReady && playerReady ? "" : " (Loading...)"}
            </p>
          </div>
        </div>
        
        {/* Status indicators */}
        <div className="flex items-center gap-2 text-sm">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            playerState === 'playing' ? 'bg-green-500/20 text-green-400' :
            playerState === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
            playerState === 'buffering' ? 'bg-blue-500/20 text-blue-400' :
            playerState === 'error' ? 'bg-red-500/20 text-red-400' :
            'bg-slate-500/20 text-slate-400'
          }`}>
            {playerState}
          </div>
          {snapshot && (
            <div className="text-xs text-slate-400">
              v{snapshot.version}
            </div>
          )}
        </div>
      </div>

      {/* URL Input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="Paste YouTube URL or video ID..."
          className="flex-1 bg-slate-800/70 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleLoadUrl();
          }}
        />
        <button
          onClick={handleLoadUrl}
          disabled={!urlInput.trim() || !ytReady}
          className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 disabled:from-slate-600 disabled:to-slate-700 text-white px-6 py-3 rounded-xl font-medium transition-all shadow-lg disabled:cursor-not-allowed"
        >
          Load
        </button>
      </div>

      {/* Video Player Container */}
      <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl">
        <div 
          ref={containerRef} 
          className="w-full aspect-video"
          style={{ minHeight: '360px' }}
        />
        
        {/* Loading overlay */}
        {(!ytReady || !playerReady) && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-white font-medium">
                {!ytReady ? "Loading YouTube API..." : "Initializing player..."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      {snapshot?.id && playerReady && (
        <div className="space-y-3">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-slate-400">
              <span>{formatTime(currentPosition)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            <div className="relative">
              <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-red-500 to-pink-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentPosition}
                onChange={(e) => handleSeek(Number(e.target.value))}
                className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
              />
            </div>
          </div>

          {/* Control buttons */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => handleSeek(Math.max(0, currentPosition - 10))}
              className="bg-slate-800/70 hover:bg-slate-700/70 text-white p-3 rounded-xl border border-slate-700/50 transition-all"
              title="Rewind 10s"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z"/>
              </svg>
            </button>

            <button
              onClick={handlePlayPause}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white p-4 rounded-xl shadow-lg transition-all scale-110"
              title={snapshot.paused ? "Play" : "Pause"}
            >
              {snapshot.paused ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 5v10l8-5-8-5z"/>
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 4h3v12H5V4zm7 0h3v12h-3V4z"/>
                </svg>
              )}
            </button>

            <button
              onClick={() => handleSeek(Math.min(duration, currentPosition + 10))}
              className="bg-slate-800/70 hover:bg-slate-700/70 text-white p-3 rounded-xl border border-slate-700/50 transition-all"
              title="Forward 10s"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"/>
              </svg>
            </button>
          </div>

          {/* Sync info */}
          <div className="text-center text-xs text-slate-500">
            {snapshot.paused ? "Paused" : "Playing"} • 
            Position: {formatTime(snapshot.positionSec)} • 
            Server sync: {clockSkewMsRef.current}ms
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to compute expected position based on server state
function computeExpectedPosition(
  snap: { paused: boolean; positionSec: number; startedAtMs: number },
  serverTimeMs: number,
  clockSkewMs: number
): number {
  if (!snap || snap.paused) return snap?.positionSec || 0;
  
  // Calculate how much time has passed since the server's startedAtMs
  const elapsedMs = Date.now() - serverTimeMs - clockSkewMs;
  return Math.max(0, (snap.positionSec || 0) + elapsedMs / 1000);
}

// Helper function to format time in MM:SS format
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}